<template>
  <div v-show="this.$store.getters.getShowWorkModal" class="work-modal">
    <div class="work-modal-overlay" @click="toggleShowWorkModal()"></div>
    <div class="work-modal-container">
      <div class="modal-content">
        <!-- Close Button -->
        <button class="close-btn" @click="toggleShowWorkModal()" aria-label="Close modal">
          <feather type="x" size="24"></feather>
        </button>

        <div class="modal-grid">
          <!-- Left Column: Image Carousel -->
          <div class="image-column">
            <div class="carousel-container" v-if="hasImages">
              <button class="carousel-btn prev" @click.stop="prevImage" aria-label="Previous image"
                v-if="imageCount > 1">
                <feather type="chevron-left" size="24"></feather>
              </button>
              <div class="carousel-image-wrapper">
                <img :src="currentImage" :alt="selectedWork.title + ' screenshot'" @load="imageLoaded = true"
                  @error="imageLoaded = true" />
                <div v-if="!imageLoaded" class="image-loading">
                  <LoadingSpinner />
                </div>
              </div>
              <button class="carousel-btn next" @click.stop="nextImage" aria-label="Next image" v-if="imageCount > 1">
                <feather type="chevron-right" size="24"></feather>
              </button>
              <div class="carousel-dots" v-if="imageCount > 1">
                <span v-for="(url, index) in selectedWork.images.urls" :key="index" class="dot"
                  :class="{ active: currentImageIndex === index }" @click="currentImageIndex = index"></span>
              </div>
            </div>
            <div v-else class="image-placeholder">
              <span>🖼️</span>
            </div>
          </div>

          <!-- Right Column: Information -->
          <div class="info-column">
            <div class="info-content">
              <h2 class="project-title">{{ selectedWork.title }}</h2>

              <div class="section overview">
                <span class="section-label">OVERVIEW</span>
                <p class="description">{{ selectedWork.about }}</p>
              </div>

              <div class="section stack">
                <span class="section-label">CORE STACK</span>
                <div class="tech-tags">
                  <span v-for="stack in selectedWork.stacks" :key="stack" class="tech-tag">
                    <span class="dot" :style="{ backgroundColor: getTechColor(stack) }"></span>
                    {{ stack.toUpperCase() }}
                  </span>
                </div>
              </div>

              <div class="actions">
                <a v-if="selectedWork.links.website" :href="selectedWork.links.website" rel="noreferrer noopener"
                  target="_blank" class="btn btn-primary">
                  Live Demo
                </a>
                <a v-if="selectedWork.links.github" :href="selectedWork.links.github" rel="noreferrer noopener"
                  target="_blank" class="btn btn-secondary">
                  View Code
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/LoadingSpinner";
import { getTechColor } from "@/constants/techColors";

export default {
  name: "WorkModal",
  components: { LoadingSpinner },
  props: {
    selectedWork: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentImageIndex: 0,
      imageLoaded: false
    };
  },
  computed: {
    hasImages: function () {
      return this.selectedWork.images &&
        this.selectedWork.images.urls &&
        this.selectedWork.images.urls.length > 0;
    },
    imageCount: function () {
      return this.hasImages ? this.selectedWork.images.urls.length : 0;
    },
    currentImage: function () {
      if (this.hasImages) {
        return this.selectedWork.images.urls[this.currentImageIndex];
      }
      return '';
    }
  },
  watch: {
    selectedWork: {
      handler: function () {
        this.currentImageIndex = 0;
        this.imageLoaded = false;
      },
      deep: true
    }
  },
  methods: {
    toggleShowWorkModal: function () {
      this.$store.commit("toggleShowWorkModal");
    },
    nextImage: function () {
      this.imageLoaded = false;
      this.currentImageIndex = (this.currentImageIndex + 1) % this.imageCount;
    },
    prevImage: function () {
      this.imageLoaded = false;
      this.currentImageIndex = (this.currentImageIndex - 1 + this.imageCount) % this.imageCount;
    },
    getTechColor: function (tech) {
      return getTechColor(tech);
    }
  }
};
</script>

<style lang="scss">
.work-modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.work-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.modal-content {
  position: relative;
  z-index: 1001;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  background: white;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 40px;
  height: 40px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: #e5e5e5;
  }

  svg {
    color: #6b7280;
  }
}

.modal-grid {
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;
  max-height: 90vh;

  @media (min-width: 1024px) {
    grid-template-columns: 1.2fr 0.8fr;
  }
}

// Image Column
.image-column {
  background: #1a365d;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;

  .carousel-container {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .carousel-btn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
      width: 40px;
      height: 40px;
      border: none;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      transition: all 0.2s ease;

      &:hover {
        background: white;
        transform: translateY(-50%) scale(1.05);
      }

      &.prev {
        left: 12px;
      }

      &.next {
        right: 12px;
      }

      svg {
        color: #374151;
      }
    }

    .carousel-image-wrapper {
      width: 100%;
      height: 100%;
      min-height: 400px;
      border-radius: 12px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
      background: #f3f4f6;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .image-loading {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(243, 244, 246, 0.9);
      }
    }

    .carousel-dots {
      display: flex;
      gap: 8px;
      margin-top: 16px;

      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        transition: all 0.2s ease;

        &.active {
          background: white;
          transform: scale(1.2);
        }

        &:hover {
          background: rgba(255, 255, 255, 0.8);
        }
      }
    }
  }

  .image-placeholder {
    width: 100%;
    height: 100%;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
    border-radius: 12px;

    span {
      font-size: 80px;
      opacity: 0.4;
    }
  }
}

// Info Column
.info-column {
  background: white;
  overflow-y: auto;
  padding: 32px;

  .info-content {
    max-width: 100%;
  }

  .project-title {
    font-family: 'Inter', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: var(--color07);
    margin: 0 0 24px 0;
    line-height: 1.3;
  }

  .section {
    margin-bottom: 28px;

    .section-label {
      display: block;
      font-family: 'Inter', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 1px;
      color: #9ca3af;
      margin-bottom: 12px;
      text-transform: uppercase;
    }

    .description {
      font-family: 'Inter', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 15px;
      line-height: 1.7;
      color: #374151;
      margin: 0;
    }

    .tech-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      .tech-tag {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.5px;
        color: #374151;

        .dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-right: 8px;
          flex-shrink: 0;
        }
      }
    }
  }

  .actions {
    display: flex;
    gap: 12px;
    margin-top: 32px;

    .btn {
      flex: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 12px 24px;
      border-radius: 8px;
      font-family: 'Inter', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 14px;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s ease;
      cursor: pointer;

      &.btn-primary {
        background-color: var(--color00);
        color: #1f2937;
        border: none;

        &:hover {
          background-color: color-mix(in srgb, var(--color00) 90%, black);
          transform: translateY(-1px);
        }
      }

      &.btn-secondary {
        background-color: #1f2937;
        color: white;
        border: none;

        &:hover {
          background-color: #111827;
          transform: translateY(-1px);
        }
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 1023px) {
  .modal-content {
    max-height: 95vh;
  }

  .info-column {
    padding: 24px;
  }

  .project-title {
    font-size: 24px;
  }
}

@media (max-width: 640px) {
  .modal-content {
    width: 95%;
    border-radius: 16px;
  }

  .image-column {
    padding: 16px;
  }

  .carousel-image-wrapper {
    min-height: 250px;
  }

  .info-column {
    padding: 20px;
  }

  .project-title {
    font-size: 20px;
    margin-bottom: 16px;
  }

  .actions {
    flex-direction: column;
  }
}
</style>
